import React, { useEffect } from 'react'

import {
  Autocomplete,
  AutocompleteItem,
  Button,
  cn,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@nextui-org/react'
import Image from 'next/image'
import { zodResolver } from '@hookform/resolvers/zod'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'
import { toast } from 'react-toastify'
import get from 'lodash/get'
import { useAccount } from 'wagmi'
import Swal from 'sweetalert2'

import ICONS from '@/assets/icons'
import { useBecomeKOLMutation } from '@/redux/api/kols'
import { useLazyLinkAcountQuery } from '@/redux/api/auth/authW3'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { handleShowMessage } from '@/utils'
import { ConnectWalletButton } from '@/components/shared/ConnectWalletButton'
import { COUNTRIES } from '@/utils/countries'
import useIsMobile from '@/hooks/useIsMobile'
import { useGetProfileQuery } from '@/redux/api/profile/profileW3'
import { tempActions } from '@/redux/reducers/temp/temp.reducer'

const formSchema = z.object({
  xUrl: z.string().min(1, 'Required').url(),
  telegramAccount: z.string().min(1, 'Required'),
  walletAddress: z.string().min(1, 'Required'),
  description: z.string().optional(),
  socialMediaUrl: z.string().url().optional(),
  country: z.string().min(1, 'Required'),
})

export const ModalBecomeKOL = ({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) => {
  const user = useAppSelector((state) => state.root.auth.user)
  const dispatch = useAppDispatch()
  const checkIsLinkX = useAppSelector((state) => state.temp.isLinkX)
  const { data: userProfile, refetch } = useGetProfileQuery({ userId: String(user?.id) }, { skip: !user?.id })
  const { address } = useAccount()
  const isMobile = useIsMobile()
  const {
    handleSubmit,
    formState: { errors, isValid },
    control,
    watch,
    setValue,
    reset,
  } = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      xUrl: undefined,
      telegramAccount: undefined,
      walletAddress: undefined,
      description: '',
      socialMediaUrl: undefined,
      country: undefined,
    },
  })

  const [becomeKOL, { isLoading, isSuccess, error }] = useBecomeKOLMutation()
  useEffect(() => {
    if (isSuccess) {
      toast.success('Request successfully')
      onClose()

      Swal.mixin({
        background: '#0A0F1A',
        color: '#fff',
        confirmButtonColor: '#432D8D',
      }).fire({
        title: 'Request sent!',
        text: 'We’re reviewing your submission within 48 hours!',
        icon: 'success',
        customClass: {
          confirmButton: 'bg-btn-gradient px-10',
        },
      })
    }
  }, [isSuccess])

  useEffect(() => {
    if (error) {
      toast.error(handleShowMessage(get(error, 'data.message')) || 'Something went wrong, please try again.')
      onClose()
    }
  }, [error])

  useEffect(() => {
    const findSocial = userProfile?.socials?.find((it) => it.provider === 'twitter')
    setValue('xUrl', findSocial?.profileUrl || '')
    setValue('walletAddress', userProfile?.connectedWalletAddress || address || '')
  }, [userProfile, address, isOpen])

  const [trigger, { data: accountData, isFetching, isLoading: isLoadingLink }] = useLazyLinkAcountQuery()
  const handleLinkAccount = () => {
    trigger({
      provider: 'twitter',
      redirectUri: window.location.origin + window.location.pathname,
    })
  }

  useEffect(() => {
    if (accountData) {
      window.open(accountData.url, '_blank')
      dispatch(tempActions.setIsLinkX(true))
    }
  }, [accountData])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        const findSocial = userProfile?.socials?.find((it) => it.provider === 'twitter')
        if (!findSocial && checkIsLinkX) {
          refetch()
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
  }, [checkIsLinkX])

  useEffect(() => {
    if (!isOpen) {
      reset()
    }
  }, [isOpen])

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    becomeKOL({
      description: data.description || '',
      socialMediaUrl: data.socialMediaUrl || '',
      telegramAccount: data.telegramAccount,
      xUrl: data.xUrl,
      walletAddress: data.walletAddress || '',
      country: data.country,
    })
  }

  return (
    <Modal
      className="border border-white/10 bg-ss-dark"
      hideCloseButton
      size="3xl"
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      classNames={{
        base: 'overflow-visible max-h-[100%]',
      }}
      backdrop="blur"
      // onOpenChange={toggleOpen}
    >
      <ModalContent>
        <ModalHeader className="flex items-center gap-6 text-white">
          <Image
            onClick={onClose}
            className="cursor-pointer"
            src={ICONS.icCloseWhite}
            alt="follow"
            width={22}
            height={22}
          />
          <h2 className="text-base font-medium">Become KOLs</h2>
          <Button
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
            className={cn(
              'ml-auto rounded-2xl  text-sm font-medium text-white hidden md:block',
              isValid ? 'bg-btn-gradient' : 'bg-[#151531]',
            )}
          >
            Submit
          </Button>
        </ModalHeader>
        <ModalBody className="flex flex-col gap-4 pb-4 overflow-y-auto">
          {watch('xUrl') ? (
            <div className="flex gap-2 items-center flex-wrap">
              <Controller
                render={({ field }) => (
                  <div className="w-full md:flex-1">
                    <Input
                      size={isMobile ? 'md' : 'lg'}
                      {...field}
                      disabled
                      label={<span className="text-sm text-ss-grayscale-500">URL X account</span>}
                      labelPlacement="outside"
                      placeholder="Enter link"
                      classNames={{
                        inputWrapper:
                          'border border-white/10 bg-ss-dark data-[hover=true]:bg-transparent group-data-[focus=true]:outline-none group-data-[focus=true]:bg-transparent',
                        input: ['bg-transparent', ' !text-ss-grayscale-500 dark:!text-ss-grayscale-500'],
                      }}
                    />
                  </div>
                )}
                name="xUrl"
                control={control}
              />
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-ss-grayscale-500">URL X account</span>
              <div>
                <Button isLoading={isFetching || isLoadingLink} onPress={handleLinkAccount} variant="bordered">
                  Link to account X
                </Button>
              </div>
              {errors?.xUrl && <div className="text-tiny text-danger">{errors?.xUrl?.message}</div>}
            </div>
          )}
          <ul style={{ listStyleType: 'disc' }} className="pl-4   ">
            <li className="text-xs text-ss-grayscale-500">Your X account should have at least 5K user</li>
            <li className="text-xs text-ss-grayscale-500">Your X account should be active for the past 28 days</li>
          </ul>
          {watch('walletAddress') ? (
            <Controller
              render={({ field }) => (
                <Input
                  size="lg"
                  {...field}
                  label={<span className="text-sm text-ss-grayscale-500">Wallet address</span>}
                  labelPlacement="outside"
                  placeholder="Enter wallet address"
                  disabled
                  isInvalid={!!errors.walletAddress}
                  errorMessage={errors.walletAddress?.message}
                  classNames={{
                    inputWrapper:
                      'border border-white/10 bg-ss-dark data-[hover=true]:bg-transparent group-data-[focus=true]:outline-none group-data-[focus=true]:bg-transparent',
                    input: ['bg-transparent', ' !text-ss-grayscale-500 dark:!text-ss-grayscale-500'],
                  }}
                />
              )}
              name="walletAddress"
              control={control}
            />
          ) : (
            <div className="flex flex-col gap-2">
              <span className="text-sm text-ss-grayscale-500">Wallet address</span>
              <div>
                <ConnectWalletButton
                  className={cn('ml-auto rounded-2xl  text-sm font-medium text-white bg-btn-gradient')}
                />
              </div>
              {errors?.walletAddress && <div className="text-tiny text-danger">{errors?.walletAddress?.message}</div>}
            </div>
          )}

          <Controller
            render={({ field }) => (
              <Input
                size={isMobile ? 'md' : 'lg'}
                {...field}
                label={<span className="text-sm text-ss-grayscale-500">Telegram account</span>}
                labelPlacement="outside"
                placeholder="Enter nickname"
                isInvalid={!!errors.telegramAccount}
                errorMessage={errors.telegramAccount?.message}
                classNames={{
                  inputWrapper:
                    'border border-white/10 bg-ss-dark data-[hover=true]:bg-transparent group-data-[focus=true]:outline-none group-data-[focus=true]:bg-transparent',
                  input: ['bg-transparent', '!text-white dark:!text-white'],
                }}
              />
            )}
            name="telegramAccount"
            control={control}
          />

          <Controller
            render={({ field }) => (
              <div>
                <Autocomplete
                  size={isMobile ? 'md' : 'lg'}
                  aria-label="country"
                  classNames={{
                    popoverContent: 'bg-ss-dark text-white border border-white/10',
                    base: 'text-white',
                  }}
                  inputProps={{
                    classNames: {
                      inputWrapper: [
                        'bg-transparent',
                        'border-white/10 data-[hover=true]:border-white/10 group-data-[focus=true]:border-white/10',
                      ],
                    },
                  }}
                  placeholder="Select country"
                  labelPlacement="outside"
                  variant="bordered"
                  defaultItems={COUNTRIES}
                  label={<span className="text-sm text-ss-grayscale-500">Select country</span>}
                  value={field.value || ''}
                  onSelectionChange={(v) => {
                    const value = v || ''
                    setValue('country', value as string, { shouldValidate: true })
                  }}
                  selectedKey={field.value || ''}
                >
                  {(item) => <AutocompleteItem key={item.name}>{item.name}</AutocompleteItem>}
                </Autocomplete>

                {errors.country?.message && (
                  <span className="text-danger text-tiny ml-1">{errors.country?.message}</span>
                )}
              </div>
            )}
            name="country"
            control={control}
          />

          <Controller
            render={({ field }) => (
              <Textarea
                {...field}
                label={<span className="text-sm text-ss-grayscale-500">Additional information</span>}
                variant="bordered"
                labelPlacement="outside"
                placeholder="Enter your information"
                isInvalid={!!errors.description}
                errorMessage={errors.description?.message}
                classNames={{
                  inputWrapper:
                    'border border-white/10 data-[hover=true]:border-white/10 group-data-[focus=true]:border-white/10 bg-ss-dark data-[hover=true]:bg-transparent group-data-[focus=true]:bg-transparent',
                  input: ['bg-transparent', '!text-white dark:!text-white'],
                }}
              />
            )}
            name="description"
            control={control}
          />
          <Controller
            render={({ field }) => (
              <Input
                size={isMobile ? 'md' : 'lg'}
                {...field}
                label={<span className="text-sm text-ss-grayscale-500">Social Link</span>}
                labelPlacement="outside"
                placeholder="Enter link social"
                isInvalid={!!errors.socialMediaUrl}
                errorMessage={errors.socialMediaUrl?.message}
                classNames={{
                  inputWrapper:
                    'border border-white/10 bg-ss-dark data-[hover=true]:bg-transparent group-data-[focus=true]:outline-none group-data-[focus=true]:bg-transparent',
                  input: ['bg-transparent', '!text-white dark:!text-white'],
                }}
              />
            )}
            name="socialMediaUrl"
            control={control}
          />
        </ModalBody>
        <ModalFooter className="md:hidden">
          <Button
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
            className={cn(
              'w-full rounded-2xl text-sm font-medium text-white md:hidden',
              isValid ? 'bg-btn-gradient' : 'bg-[#151531]',
            )}
          >
            Submit
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
